import React, { createContext, useContext, useState, ReactNode } from "react";

export interface WorkflowLock {
  workflowId: string;
  isLocked: boolean;
  isOwner: boolean;
  owner?: string;
  lockId?: string;
  lockedBy?: string;
  lockedByName?: string;
  lockedAt?: string;
  lockExpiresAt?: string;
  nextRefreshAt?: string;
  refreshIntervalSeconds?: number;
}

interface CollaborationContextValue {
  lock: WorkflowLock | null;
  setLock: React.Dispatch<React.SetStateAction<WorkflowLock | null>>;
  renewalInterval: NodeJS.Timeout | null;
  setRenewalInterval: React.Dispatch<
    React.SetStateAction<NodeJS.Timeout | null>
  >;
}

const CollaborationContext = createContext<
  CollaborationContextValue | undefined
>(undefined);

interface CollaborationProviderProps {
  children: ReactNode;
}

export const CollaborationProvider: React.FC<CollaborationProviderProps> = ({
  children,
}) => {
  const [lock, setLock] = useState<WorkflowLock | null>(null);
  const [renewalInterval, setRenewalInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  const value: CollaborationContextValue = {
    lock,
    setLock,
    renewalInterval,
    setRenewalInterval,
  };

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  );
};

/**
 * Hook to access collaboration state
 */
export const useCollaboration = (): CollaborationContextValue => {
  const context = useContext(CollaborationContext);
  if (context === undefined) {
    throw new Error(
      "useCollaboration must be used within a CollaborationProvider",
    );
  }
  return context;
};

/**
 * Optional version of useCollaboration that returns null when outside provider.
 * Useful for hooks like useProjects that may be used both inside and outside
 * the CollaborationProvider (e.g., dashboard vs editor).
 */
export const useCollaborationOptional =
  (): CollaborationContextValue | null => {
    return useContext(CollaborationContext) ?? null;
  };
